/**
 * LegCardActions — action buttons row for LegCard (chart, box score, roster intel, add to builder)
 */

import { SPORTSBOOKS } from '@config/constants';
import type { LeagueKey } from '@parlay-picker/shared/league';
import type { BestOddsResult } from '@parlay-picker/shared/oddsUtils';
import { getLegAwayHome } from '@parlay-picker/shared/teamParsing';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { AlertTriangle, BarChart3, Check, Plus, TableProperties, UsersRound } from 'lucide-react';
import type React from 'react';
import type { MouseEvent } from 'react';

// =============================================================================
// Types
// =============================================================================

interface LegActionButtonsProps {
  /** Player name (null for team bets) */
  player: string | null | undefined;
  /** Team name */
  team: string | null | undefined;
  /** Opponent team name */
  opponentTeam: string | null | undefined;
  /** True when `team` plays at home */
  isHomeGame?: boolean | null;
  /** Game ID for box score / lineup modal */
  gameId: string | null | undefined;
  /** Whether the leg is expired */
  expired: boolean;
  /** Whether picks are streaming (hides add-to-builder) */
  isStreaming: boolean | undefined;
  /** Whether selection is enabled */
  enableSelection: boolean;
  /** Whether a parent parlay is present (required for add-to-builder) */
  hasParlay: boolean;
  /** Whether this leg is already in the builder */
  isInBuilder: boolean;
  /** Whether adding this leg would be blocked */
  wouldBeBlocked: { canAdd: boolean; blockReason?: string };
  /** Resolved team abbreviation (pre-computed) */
  teamAbbr: string | null | undefined;
  /** Resolved opponent abbreviation */
  opponentAbbr: string | null | undefined;
  /** League key */
  leagueKey: LeagueKey;
  /** Whether the leg has recent game data for chart */
  hasRecentGames: boolean;
  /** Whether the leg has team recent game data (game-level props) */
  hasTeamRecentGames: boolean;
  /** Whether this is a game-level prop */
  isGameLevelProp: boolean;
  /** Odds comparison for this leg */
  legComparison: BestOddsResult | undefined;
  /** Whether odds are locked */
  oddsLocked: boolean;
  /** Current leg odds */
  legOdds: number | string | null | undefined;
  /** Handler for chart click */
  onChartClick: (e: MouseEvent<HTMLButtonElement>) => void;
  /** Handler for add-to-builder click */
  onAddToBuilder: (e: MouseEvent<HTMLButtonElement>) => void;
  /** Handler for comparison modal open */
  onOpenComparison: () => void;
}

// =============================================================================
// LegCardActions
// =============================================================================

export function LegCardActions({
  player,
  team,
  opponentTeam,
  isHomeGame,
  gameId,
  expired,
  isStreaming,
  enableSelection,
  hasParlay,
  isInBuilder,
  wouldBeBlocked,
  teamAbbr,
  opponentAbbr,
  leagueKey,
  hasRecentGames,
  hasTeamRecentGames,
  isGameLevelProp,
  legComparison,
  oddsLocked,
  legOdds,
  onChartClick,
  onAddToBuilder,
  onOpenComparison,
}: LegActionButtonsProps): React.ReactElement {
  const matchup = getLegAwayHome({ team, opponentTeam, isHomeGame });
  return (
    <div className="flex flex-col @2xl:flex-row items-end @2xl:items-center gap-1 @2xl:gap-2">
      <div className="flex flex-row items-center gap-1.5">
        {/* Compact sportsbook odds stack — tappable to open comparison */}
        {!oddsLocked && legComparison && legComparison.allBooks.length > 1 && (
          <button
            type="button"
            onClick={onOpenComparison}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-theme-bg/80 hover:bg-theme-border transition order-first @2xl:order-last"
            title={`Compare odds across ${legComparison.allBooks.length} sportsbooks`}
          >
            <span className="flex -space-x-1">
              {legComparison.allBooks.slice(0, 4).map((book) => (
                <span
                  key={book.sportsbook}
                  className={`w-2.5 h-2.5 rounded-full ring-1 ring-theme-bg ${SPORTSBOOKS[book.sportsbook]?.dot || 'bg-theme-text-secondary'}`}
                />
              ))}
            </span>
            {legComparison.allBooks.length > 4 && (
              <span className="text-[9px] text-theme-text-tertiary">
                +{legComparison.allBooks.length - 4}
              </span>
            )}
            {legComparison.hasBetterLine && (
              <span className="text-[11px] text-theme-success font-medium">
                {legComparison.bestBook.odds}
              </span>
            )}
          </button>
        )}
        <span className="text-theme-success font-medium">{legOdds}</span>
      </div>
      <div className="flex flex-row items-center gap-1">
        {/* Chart icon button — always visible (even on expired legs) */}
        {(hasRecentGames || (isGameLevelProp && hasTeamRecentGames)) && (
          <button
            type="button"
            onClick={onChartClick}
            className="p-1.5 rounded-lg bg-theme-border hover:bg-theme-text-tertiary text-theme-text-secondary hover:text-theme-text transition"
            title={isGameLevelProp ? 'View team history' : 'View stats chart'}
          >
            <BarChart3 className="w-4 h-4" />
          </button>
        )}
        {/* Box score button — visible when game has live/final data */}
        {gameId && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              useGlobalShortcutsStore
                .getState()
                .openBoxScore(gameId, [player || team || ''].filter(Boolean), leagueKey, {
                  homeTeam: matchup?.home,
                  awayTeam: matchup?.away,
                });
            }}
            className="p-1.5 rounded-lg bg-theme-border hover:bg-theme-text-tertiary text-theme-text-secondary hover:text-theme-text transition"
            title="View box score"
          >
            <TableProperties className="w-4 h-4" />
          </button>
        )}
        {/* Roster Intel button — always visible (even on expired legs) */}
        {team && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              useGlobalShortcutsStore.getState().openGameModal(
                'lineup',
                gameId
                  ? {
                      gameId,
                      homeTeam: matchup?.home,
                      awayTeam: matchup?.away,
                      leagueKey,
                    }
                  : undefined,
                [player || ''].filter(Boolean),
                teamAbbr ?? undefined,
                opponentAbbr ?? undefined,
              );
            }}
            className="p-1.5 rounded-lg bg-theme-border hover:bg-theme-text-tertiary text-theme-text-secondary hover:text-theme-text transition"
            title="View team intel"
          >
            <UsersRound className="w-4 h-4" />
          </button>
        )}
        {/* Add to builder button — hidden when expired or streaming */}
        {!expired && !isStreaming && enableSelection && hasParlay && (
          <button
            type="button"
            onClick={onAddToBuilder}
            disabled={isInBuilder}
            className={`p-1.5 rounded-lg transition ${
              isInBuilder
                ? 'bg-theme-success/30 text-theme-success cursor-default'
                : !wouldBeBlocked.canAdd
                  ? 'bg-theme-warning/30 hover:bg-theme-warning/50 text-theme-warning'
                  : 'bg-theme-border hover:bg-theme-success text-theme-text-secondary hover:text-white'
            }`}
            title={
              isInBuilder
                ? 'Already in builder'
                : !wouldBeBlocked.canAdd
                  ? `Would conflict: ${wouldBeBlocked.blockReason}`
                  : 'Add to builder'
            }
          >
            {isInBuilder ? (
              <Check className="w-4 h-4" />
            ) : !wouldBeBlocked.canAdd ? (
              <AlertTriangle className="w-4 h-4" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
